import {FC, useEffect, useState} from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {GET, POST, PUT} from '../../../services/api'

type RedemptionStatus = 'pending' | 'confirmed' | 'delivered' | 'canceled'

type FilterState = {
  status: string
  createdFrom: string
  createdTo: string
}

type RedemptionRow = {
  id: number
  customerName: string
  rewardTitle: string
  pointsSpent: number
  quantity: number
  status: string
  statusRaw: RedemptionStatus
  created: string
  updated: string
  reservationId: number | null
  reservationLabel: string
  hasReservation: boolean
  canConfirm: boolean
  canDeliver: boolean
  canCancel: boolean
}

const STATUS_OPTIONS = [
  {value: '', select: 'Todos'},
  {value: 'pending', select: 'Pendente'},
  {value: 'confirmed', select: 'Confirmado'},
  {value: 'delivered', select: 'Entregue'},
  {value: 'canceled', select: 'Cancelado'},
]

const STATUS_LABELS: Record<RedemptionStatus, string> = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  delivered: 'Entregue',
  canceled: 'Cancelado',
}

const INITIAL_FILTERS: FilterState = {
  status: '',
  createdFrom: '',
  createdTo: '',
}

const extractList = (response: any): any[] => {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.dados)) return response.dados
  if (Array.isArray(response)) return response
  return []
}

const RedemptionsContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<RedemptionRow[]>([])
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS)

  const setFilterField = <K extends keyof FilterState>(field: K, value: FilterState[K]) => {
    setFilters((prev) => ({...prev, [field]: value}))
  }

  const mapRows = (data: any[]): RedemptionRow[] =>
    data.map((item) => {
      const statusRaw = (item?.status || 'pending') as RedemptionStatus
      const reservationId = item?.reservationId ? Number(item.reservationId) : null
      return {
        id: Number(item?.id || 0),
        customerName: item?.customer?.name || item?.customerHotelPoints?.customer?.name || '-',
        rewardTitle: item?.reward?.title || '-',
        pointsSpent: Number(item?.pointsSpent || 0),
        quantity: Number(item?.quantity || 1),
        status: STATUS_LABELS[statusRaw] || statusRaw,
        statusRaw,
        created: item?.created
          ? moment(item.created).utcOffset(-3).format('DD/MM/YYYY HH:mm:ss')
          : '-',
        updated: item?.updated
          ? moment(item.updated).utcOffset(-3).format('DD/MM/YYYY HH:mm:ss')
          : '-',
        reservationId,
        reservationLabel: reservationId ? `#${reservationId}` : '-',
        hasReservation: !!reservationId,
        canConfirm: statusRaw === 'pending',
        canDeliver: statusRaw === 'pending' || statusRaw === 'confirmed',
        canCancel: statusRaw === 'pending' || statusRaw === 'confirmed',
      }
    })

  const loadRedemptions = async (currentFilters: FilterState = filters) => {
    setLoading(true)
    try {
      let response
      const hasFilter =
        !!currentFilters.status || !!currentFilters.createdFrom || !!currentFilters.createdTo

      if (hasFilter) {
        const payload: Record<string, string> = {}
        if (currentFilters.status) payload.status = currentFilters.status
        if (currentFilters.createdFrom) {
          payload.createdFrom = moment(currentFilters.createdFrom).startOf('day').toISOString()
        }
        if (currentFilters.createdTo) {
          payload.createdTo = moment(currentFilters.createdTo).endOf('day').toISOString()
        }
        response = await POST('/hotel-points/redemptions/filter', payload)
      } else {
        try {
          response = await GET('/hotel-points/redemptions')
        } catch {
          response = await POST('/hotel-points/redemptions/filter', {})
        }
      }

      setRows(mapRows(extractList(response)))
    } catch (error) {
      console.error(error)
      setRows([])
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Erro ao carregar os resgates.',
      })
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (item: RedemptionRow, status: 'confirmed' | 'delivered') => {
    try {
      setLoading(true)
      const response = await PUT(`/hotel-points/redemptions/${item.id}`, {status})
      if (response?.success === false) {
        throw new Error(response?.message || 'Não foi possível atualizar o resgate.')
      }
      await loadRedemptions()
      Swal.fire({
        icon: 'success',
        title: 'Resgate atualizado',
        text: status === 'confirmed' ? 'Resgate confirmado.' : 'Resgate marcado como entregue.',
      })
    } catch (error) {
      console.error(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) || 'Não foi possível atualizar o resgate.',
      })
    } finally {
      setLoading(false)
    }
  }

  const cancelRedemption = async (item: RedemptionRow) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Cancelar resgate?',
      text: 'Os pontos gastos serão devolvidos ao hóspede.',
      showCancelButton: true,
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Voltar',
    })
    if (!confirmation.isConfirmed) return

    try {
      setLoading(true)
      const response = await POST(`/hotel-points/redemptions/${item.id}/cancel`, {})
      if (response?.success === false) {
        throw new Error(response?.message || 'Não foi possível cancelar o resgate.')
      }
      await loadRedemptions()
      Swal.fire({
        icon: 'success',
        title: 'Resgate cancelado',
        text: 'Os pontos foram devolvidos.',
      })
    } catch (error) {
      console.error(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) || 'Não foi possível cancelar o resgate.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRedemptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Resgates</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Acompanhe e atualize os resgates de recompensas.
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <div className='w-100 mb-10'>
                <div className='row align-items-end g-5'>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label='Status'
                      value={filters.status}
                      change={(value: string) => setFilterField('status', value)}
                      options={STATUS_OPTIONS}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'date'}}
                      label='Data inicial'
                      value={filters.createdFrom}
                      change={(value: string) => setFilterField('createdFrom', value)}
                      maxLength={filters.createdTo}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'date'}}
                      label='Data final'
                      value={filters.createdTo}
                      change={(value: string) => setFilterField('createdTo', value)}
                      minLength={filters.createdFrom}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-3 d-flex justify-content-md-end gap-3'>
                    <Button
                      color='primary'
                      icon='fa fa-filter'
                      text='Filtrar'
                      size='small'
                      click={() => loadRedemptions()}
                      btnClass='mb-0'
                    />
                    <Button
                      color='danger'
                      icon='fa fa-eraser'
                      size='small'
                      text='Limpar'
                      click={() => {
                        setFilters(INITIAL_FILTERS)
                        loadRedemptions(INITIAL_FILTERS)
                      }}
                      btnClass='mb-0'
                    />
                  </div>
                </div>
              </div>

              <Datatable
                data={rows}
                headers={[
                  {key: 'Cliente', value: 'customerName', type: 'default'},
                  {key: 'Recompensa', value: 'rewardTitle', type: 'default'},
                  {key: 'Pontos', value: 'pointsSpent', type: 'default'},
                  {key: 'Qtd.', value: 'quantity', type: 'default'},
                  {
                    key: 'Status',
                    value: 'status',
                    type: 'badge',
                    badge: {
                      pendente: 'warning',
                      confirmado: 'info',
                      entregue: 'success',
                      cancelado: 'danger',
                    },
                  },
                  {key: 'Reserva', value: 'reservationLabel', type: 'default'},
                  {key: 'Data', value: 'created', type: 'default'},
                  {key: 'Atualizado', value: 'updated', type: 'default'},
                  {key: 'Ações', value: 'acoes', type: '', noOrder: true},
                ]}
                options={[
                  {
                    icon: 'bi bi-door-open',
                    action: (item) =>
                      window.open(
                        `/hospitality/hotel-reservation/editar/${item.reservationId}`,
                        '_blank'
                      ),
                    title: 'Abrir reserva',
                    verifyConditionItem: 'hasReservation',
                    verifyConditionValue: true,
                  },
                  {
                    icon: 'bi bi-check2-circle',
                    action: (item) => void updateStatus(item, 'confirmed'),
                    title: 'Confirmar',
                    verifyConditionItem: 'canConfirm',
                    verifyConditionValue: true,
                  },
                  {
                    icon: 'bi bi-box-seam',
                    action: (item) => void updateStatus(item, 'delivered'),
                    title: 'Marcar como entregue',
                    verifyConditionItem: 'canDeliver',
                    verifyConditionValue: true,
                  },
                  {
                    icon: 'bi bi-x-circle',
                    action: (item) => void cancelRedemption(item),
                    title: 'Cancelar resgate',
                    verifyConditionItem: 'canCancel',
                    verifyConditionValue: true,
                  },
                ]}
                color='primary'
                showControls
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const HotelPointsRedemptions: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Pontos Hotel', path: 'hotel-points', isActive: false},
          {title: 'Resgates', path: 'hotel-points/redemptions', isActive: true},
        ]}
      >
        Resgates
      </PageTitle>
      <RedemptionsContent />
    </>
  )
}

export {HotelPointsRedemptions}
